import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import HeroSlider from "../../components/public/HeroSlider";
import StatsCounter from "../../components/public/StatsCounter";
import ServicesGrid from "../../components/public/ServicesGrid";
import MembersMarquee from "../../components/public/MembersMarquee";
import NewsGrid from "../../components/public/NewsGrid";
import HomePopup from "../../components/public/HomePopup";
import { CheckCircle2, ArrowUpRight, Loader2 } from "lucide-react";

const BENEFITS = [
  "one", "two", "three", "four", "five",
];

function NewsletterForm({ lang, t }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState({ loading: false, ok: null, msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: null, msg: "" });
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, name, lang });
      const ok = data.pushed
        ? (data.double_optin ? t("home.newsletter.confirmSoon") : t("home.newsletter.subscribed"))
        : t("home.newsletter.pending");
      setStatus({ loading: false, ok: true, msg: ok });
      setEmail("");
      setName("");
    } catch (err) {
      setStatus({ loading: false, ok: false, msg: err.response?.data?.detail || t("home.newsletter.failed") });
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 max-w-xl mx-auto" data-testid="newsletter-form">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("home.newsletter.namePlaceholder")}
          className="sm:w-56 px-5 py-3.5 border border-gray-300 focus:border-[#0F2040] outline-none bg-white"
          data-testid="newsletter-name"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          className="flex-1 px-5 py-3.5 border border-gray-300 focus:border-[#0F2040] outline-none bg-white"
          data-testid="newsletter-email"
        />
        <button
          type="submit"
          disabled={status.loading}
          className="bg-[#0F2040] hover:bg-[#1A2F5C] disabled:opacity-50 text-white px-7 py-3.5 text-sm font-medium tracking-wide inline-flex items-center justify-center gap-2"
          data-testid="newsletter-submit"
        >
          {status.loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {status.loading ? t("common.sending") : t("home.newsletterCta")}
        </button>
      </div>
      {status.msg && (
        <p className={`text-sm ${status.ok ? "text-green-700" : "text-red-700"}`} data-testid="newsletter-status">
          {status.msg}
        </p>
      )}
    </form>
  );
}

export default function Home() {
  const { t, lang } = useLang();
  const [slides, setSlides] = useState([]);
  const [logos, setLogos] = useState([]);
  const [activities, setActivities] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/hero-slides").then((r) => setSlides(r.data)).catch(() => {});
    api.get("/homepage-logos").then((r) => setLogos(r.data)).catch(() => {});
    // The "Actualidad" section on the Home now shows the 3 most recent activities
    // (events under the `actividades` family). "Ver todo" links to /actividades.
    api.get("/events?family=actividades").then((r) => setActivities((r.data || []).slice(0, 3))).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Prefer the CMS override for the current language; if that field is empty
  // fall back to the ES override, then to the built-in i18n default. Works for
  // the 4 supported languages (es · fr · en · ar).
  const cms = (esField, _unused, defaultKey) => {
    const base = esField.replace(/_es$/, "");
    const override = settings?.[`${base}_${lang}`] || settings?.[esField];
    return (override && String(override).trim()) ? override : t(defaultKey);
  };

  return (
    <div data-testid="home-page">
      <HomePopup />
      <HeroSlider slides={slides} />

      {/* Welcome */}
      <section className="bg-white py-24" data-testid="welcome-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C8102E] mb-5">
              <span className="editorial-rule mr-3 align-middle"></span>
              {cms("home_welcome_kicker_es", "home_welcome_kicker_fr", "home.welcomeKicker")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0F2040] leading-[1.1] mb-7">
              {cms("home_welcome_title_es", "home_welcome_title_fr", "home.welcomeTitle")}
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg mb-8">
              {cms("home_welcome_body_es", "home_welcome_body_fr", "home.welcomeBody")}
            </p>
            <Link
              to={settings?.home_welcome_cta_url || "/la-camara"}
              className="inline-flex items-center gap-2 text-[#0F2040] font-medium border-b border-[#0F2040] pb-1 hover:text-[#C8102E] hover:border-[#C8102E] transition-colors"
              data-testid="welcome-cta"
            >
              {cms("home_welcome_cta_label_es", "home_welcome_cta_label_fr", "nav.camaraOverview")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={settings?.home_welcome_image || "https://images.unsplash.com/photo-1758520144420-3e5b22e9b9a4?auto=format&fit=crop&w=1200&q=85"}
                alt=""
                className="w-full h-full object-cover"
                style={{ objectPosition: `${settings?.home_welcome_image_focal_x ?? 50}% ${settings?.home_welcome_image_focal_y ?? 50}%` }}
                data-testid="welcome-image"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#C8102E] text-white px-6 py-5 max-w-xs hidden md:block" data-testid="welcome-year-badge">
              <p className="font-serif text-3xl">{settings?.home_welcome_year || "1886"}</p>
              <p className="text-xs uppercase tracking-widest text-white/80 mt-1">
                {settings?.[`home_welcome_year_label_${lang}`]
                  || settings?.home_welcome_year_label_es
                  || (lang === "es" ? "Año de fundación"
                      : lang === "fr" ? "Année de fondation"
                      : lang === "en" ? "Founding year"
                      : "سنة التأسيس")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <StatsCounter stats={settings} t={(k) => k === "home.statsTitle" ? cms("home_stats_title_es", "home_stats_title_fr", "home.statsTitle") : t(k)} />

      {/* Manifesto */}
      <section className="relative bg-[#0F2040] text-white py-28 overflow-hidden">
        <img
          src={settings?.home_manifesto_bg_image || "https://images.unsplash.com/photo-1582919534700-acf2374f10d3?auto=format&fit=crop&w=2000&q=80"}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cecit-gold mb-7">
            {cms("home_manifesto_kicker_es", "home_manifesto_kicker_fr", "home.manifestoKicker")}
          </p>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-7 italic">
            {cms("home_manifesto_quote_es", "home_manifesto_quote_fr", "home.manifestoTitle")}
          </h2>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
            {cms("home_manifesto_body_es", "home_manifesto_body_fr", "home.manifestoBody")}
          </p>
        </div>
      </section>

      <ServicesGrid />

      <MembersMarquee logos={logos} />

      {/* Benefits */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-14">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C8102E] mb-4">
              {cms("home_benefits_kicker_es", "home_benefits_kicker_fr", "home.benefitsKicker")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0F2040] leading-[1.1] mb-7">
              {cms("home_benefits_title_es", "home_benefits_title_fr", "home.benefitsTitle")}
            </h2>
            <Link to="/ventajas-socio" className="inline-flex items-center bg-[#C8102E] hover:bg-[#A30D25] text-white px-7 py-3.5 text-sm font-medium tracking-wide transition-colors">
              {cms("home_benefits_cta_label_es", "home_benefits_cta_label_fr", "nav.becomeMember")}
            </Link>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-1 gap-3">
            {BENEFITS.map((k, i) => (
              <div key={k} className="flex items-start gap-4 border-b border-gray-100 pb-4">
                <span className="text-cecit-gold font-serif text-2xl w-10 shrink-0">0{i + 1}</span>
                <div className="flex-1">
                  <p className="text-[#0F2040] font-medium leading-relaxed">{t(`benefits.${k}`)}</p>
                </div>
                <CheckCircle2 className="w-5 h-5 text-[#C8102E] mt-1" strokeWidth={1.5} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <NewsGrid
        items={activities}
        basePath="/actividades"
        kickerKey="home.activitiesKicker"
        titleKey="home.activitiesTitle"
        excerptFields={["description", "excerpt"]}
      />

      {/* CTAs */}
      <section className="bg-white">
        <div className="grid md:grid-cols-2">
          <div className="bg-[#0F2040] text-white p-14 md:p-20">
            <h3 className="font-serif text-3xl md:text-4xl mb-5 leading-tight">{t("home.ctaJoinTitle")}</h3>
            <p className="text-white/75 mb-8 leading-relaxed">{t("home.ctaJoinBody")}</p>
            <Link to="/ventajas-socio" className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#A30D25] text-white px-7 py-3.5 text-sm font-medium" data-testid="cta-join">
              {t("home.ctaJoinBtn")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="bg-gray-50 p-14 md:p-20">
            <h3 className="font-serif text-3xl md:text-4xl text-[#0F2040] mb-5 leading-tight">{t("home.ctaDirTitle")}</h3>
            <p className="text-gray-600 mb-8 leading-relaxed">{t("home.ctaDirBody")}</p>
            <Link to="/buscador-socios" className="inline-flex items-center gap-2 border border-[#0F2040] text-[#0F2040] hover:bg-[#0F2040] hover:text-white px-7 py-3.5 text-sm font-medium transition-colors" data-testid="cta-directory">
              {t("home.ctaDirBtn")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[#FAFAFA] py-20 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#0F2040] mb-4">{t("home.newsletterTitle")}</h2>
          <p className="text-gray-600 mb-8">{t("home.newsletterBody")}</p>
          <NewsletterForm lang={lang} t={t} />
        </div>
      </section>
    </div>
  );
}
